package com.fireavert.react.shared.components.nav

import com.fireavert.cache.client.Preferences
import com.fireavert.menu.client.presentation.MenuController
import com.fireavert.menu.client.presentation.MenuViewModel
import com.fireavert.react.shared.styles.ModernNavStyles
import com.fireavert.utilities.getKoinInstance
import emotion.css.cx
import kotlinx.coroutines.flow.launchIn
import kotlinx.coroutines.flow.onEach
import react.*
import react.dom.html.ReactHTML.a
import react.dom.html.ReactHTML.div
import react.dom.html.ReactHTML.img
import react.dom.html.ReactHTML.span
import web.window.WindowTarget


external interface MobileModernNavItemProps : Props {
    var onClick: () -> Unit
    var isActive: Boolean
    var iconPath: String
    var text: String
    var badgeCount: Int?
}

val MobileModernNavItem = FC<MobileModernNavItemProps> { props ->
    div {
        className = cx(
            ModernNavStyles.mobileNavItem,
            if (props.isActive) ModernNavStyles.mobileNavItemActive else null
        )
        onClick = { props.onClick() }

        img {
            src = props.iconPath
            alt = "${props.text} icon"
            className = ModernNavStyles.mobileNavItemIcon
        }

        span {
            className = ModernNavStyles.mobileNavItemText
            +props.text
        }

        props.badgeCount?.let { count ->
            if (count > 0) {
                span {
                    className = cx(
                        ModernNavStyles.navItemBadge,
                        if (props.isActive) ModernNavStyles.navItemBadgeActive else null
                    )
                    +count.toString()
                }
            }
        }
    }
}

// New props for MobileNavV2
external interface MobileNavV2Props : SideNavProps {
    var isOpen: Boolean
    var onClose: () -> Unit
}

val MobileNavV2 = FC<MobileNavV2Props> { props ->
    val viewModel = getKoinInstance<MenuViewModel>()
    val controller = getKoinInstance<MenuController>()
    val preferences = getKoinInstance<Preferences>() // Added preferences

    var showAdministrationMenuItem by useState(false)
    var dashboardNumber by useState(0)
    var propertiesNumber by useState(0)

    // New state variables for global filters
    var propertyMapState: Map<String, Int?> by useState(mapOf("All" to null))
    var defaultPropertyValueState by useState(preferences.globalProperty)
    var propertyNameState by useState(
        preferences.globalProperty?.let { propId ->
            propertyMapState.entries.find { it.value == propId }?.key
        } ?: propertyMapState.entries.find { it.value == null }?.key ?: "Properties"
    )

    useEffectOnceWithCleanup {
        val viewStateJob = viewModel.viewState.onEach { viewState ->
            showAdministrationMenuItem = viewState.showAdministrationMenuItem
            dashboardNumber = viewState.dashboardNumber
            propertiesNumber = viewState.propertiesNumber

            // Add property map update
            if (propertyMapState != viewState.propertyMap && viewState.propertyMap.isNotEmpty()) {
                propertyMapState = viewState.propertyMap
            }
        }.launchIn(viewModel.scope)

        onCleanup {
            viewStateJob.cancel()
        }
        controller.didLoad()
    }

    // Additional effects for global property state
    useEffect(dependencies = arrayOf(preferences.globalProperty)) {
        if (defaultPropertyValueState != preferences.globalProperty) {
            defaultPropertyValueState = preferences.globalProperty
        }
    }

    useEffect(dependencies = arrayOf(defaultPropertyValueState, propertyMapState)) {
        val newName = if (defaultPropertyValueState == null) {
            "All"
        } else {
            propertyMapState.entries.find { it.value == defaultPropertyValueState }?.key
                ?: "All"
        }

        if (propertyNameState != newName) {
            propertyNameState = newName
        }
    }

    div {
        className = cx(
            ModernNavStyles.mobileNavDrawer,
            if (props.isOpen) ModernNavStyles.mobileNavDrawerOpen else null
        )

        div {
            className = ModernNavStyles.mobileNavHeader
            img {
                alt = "FireAvert Logo"
                src = "/static/logo_v2.svg"
                className = ModernNavStyles.mobileNavLogo
            }
            div {
                className = ModernNavStyles.mobileNavCloseButton
                onClick = { props.onClose() }
                +"×"
            }
        }

        // Add GlobalFilters component here, right after the header
        GlobalFilters {
            coroutineScope = viewModel.scope
            this.controller = controller
            this.preferences = preferences
            this.propertyMapState = propertyMapState
            this.defaultPropertyValueState = defaultPropertyValueState
            isCollapsed = false // Always expanded in mobile view
        }

        div {
            className = ModernNavStyles.mobileNavItems

            div {
                className = ModernNavStyles.mobileNavGroup
                div {
                    className = ModernNavStyles.mobileNavGroupLabel
                    +"MAIN"
                }
                MobileModernNavItem {
                    onClick = { controller.clickedDashboard(); props.onClose() }
                    isActive = props.activeNavOption is NavOption.Dashboard
                    iconPath = "/static/dashboard_icon_20.svg"
                    text = "Dashboard"
                }
                MobileModernNavItem {
                    onClick = { controller.clickedProperties(); props.onClose() }
                    isActive = props.activeNavOption is NavOption.Properties
                    iconPath = "/static/properties_icon_22.svg"
                    text = "Properties"
                    badgeCount = propertiesNumber
                }
                // Rest of navigation items remain the same
                MobileModernNavItem {
                    onClick = { controller.clickedReports(); props.onClose() }
                    isActive = props.activeNavOption is NavOption.Reports
                    iconPath = "/static/reports_icon.svg"
                    text = "Reports"
                }
                // Rest of navigation items remain the same
                MobileModernNavItem {
                    onClick = { controller.clickedMyFireRisk(); props.onClose() }
                    isActive = props.activeNavOption is NavOption.Reports
                    iconPath = "/static/sidebar_icons/my_fire_risk_icon.svg"
                    text = "My Fire Risk"
                }
                if (showAdministrationMenuItem) {
                    MobileModernNavItem {
                        onClick = { controller.clickedAdministration(); props.onClose() }
                        isActive = props.activeNavOption is NavOption.Administration
                        iconPath = "/static/report_icon.svg"
                        text = "Administration"
                    }
                }
            }

            // Rest of the component remains unchanged
            div {
                className = ModernNavStyles.mobileNavGroup
                div {
                    className = ModernNavStyles.mobileNavGroupLabel
                    +"SUPPORT"
                }
                MobileModernNavItem {
                    onClick = { controller.clickedHelp(); props.onClose() }
                    isActive = props.activeNavOption is NavOption.Help
                    iconPath = "/static/help_icon.svg"
                    text = "Help"
                }
                MobileModernNavItem {
                    onClick = { controller.clickedFeedback(); props.onClose() }
                    isActive = props.activeNavOption is NavOption.Feedback
                    iconPath = "/static/sidebar_icons/feedback_icon.svg"
                    text = "Send Feedback"
                }
            }
        }

        div {
            className = ModernNavStyles.mobileNavFooter
            a {
                className = ModernNavStyles.mobilePrivacyPolicyLink
                href = "https://fireavert.com/pages/privacy-policy"
                target = WindowTarget._blank
                onClick = { props.onClose() }
                +"Privacy Policy"
            }
        }
    }
}